import * as React from 'react';
import { DirectLine } from 'botframework-directlinejs';
import ReactWebChat from 'botframework-webchat';
import { initializeIcons } from '@uifabric/icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import html2canvas from 'html2canvas';
import Draggable from 'react-draggable';
import { Guid } from 'guid-typescript';
import styles from './styles.module.css'

export class IrisConstants {
  public static readonly SpinnerImageUrl = 'https://iris-fd-cpc8d6c9buafasc2.b01.azurefd.net/iris-ux/assets/images/spinner.gif';
  public static readonly LoadingImageUrl = 'https://iris-fd-cpc8d6c9buafasc2.b01.azurefd.net/iris-ux/assets/images/loading.gif';
  public static readonly ChatWindowHeaderTitle = 'Iris Copilot';
  public static readonly HelpButtonUrl = 'https://microsoft.sharepoint.com/teams/AnswersHub/SitePages/IntelligentSupport.aspx';
  public static readonly InstallTeamsUrl = 'https://teams.microsoft.com/l/app/d31e002e-6b41-4ef8-80d9-be7fb1a33e45';
  public static readonly WhatsNewUrl = 'https://microsoft.sharepoint.com/teams/AnswersHub/SitePages/WhatsNewinIris-.aspx';
  public static readonly TokenUrl =  '/api/irisbot/token';
  public static readonly ContextUrl = '/api/context/v2/user';
  public static readonly AutoCompleteUrl = '/api/autocomplete/search';
  public static readonly TelemetryUrl = '/api/telemetry/customEvent';
  public static readonly TelemetryExceptionUrl = '/api/telemetry/exception';
  public static readonly MainIconUrl = 'https://iris-fd-cpc8d6c9buafasc2.b01.azurefd.net/iris-ux/assets/images/iris-copilot-icon.svg';
}

export interface IIrisBotToken {
  conversationId: string;
  token: string;
  botAppToken: string;
  expires_in: number;
  graphToken: string;
}

export interface IIrisBotState {
  isInitializing: boolean;
  isWelcomeEventPosted: boolean;
  isOpenPanel: boolean;
  uniqueId: number;
  windowWidth: number;
  windowHeight: number;
}

export interface IIrisBotStyle {
  avatarIconUrl?: string;
  headerBackgroundColor?: string;
  userActivityBackgroundColor?: string;
  chatWindowBacgroundColor?: string;
}

export interface ISelfHelpContext {
  selfHelpEventName: string,
  messages: string[],
  activityContext: any
}

export interface IIrisBotProps {
  appName: string;
  platform: string;
  selfHelpContext?: ISelfHelpContext;
  style?: IIrisBotStyle;
  enableAutoSuggest: boolean;
  irisApiBaseUrl: string;
  acquireToken: () => Promise<string>;
  closeBot?: () => void;
  refreshIconText?: string;
  hideCloseButton?: boolean;
  enableWindowResizing?: boolean;
}

export class IrisWebChat extends React.Component<IIrisBotProps, IIrisBotState> {
  private directLine: DirectLine;
  private botAvatarView: any;
  private currentCount: number = 0;
  private irisBotToken: IIrisBotToken;
  private irisBotTokenPromise: Promise<IIrisBotToken>;
  private acquireTokenPromise: Promise<string>;
  private authToken: string;
  private store: any;
  private showAutoCompleteResult = true;

  constructor(props: IIrisBotProps, _state: IIrisBotState) {
    super(props);
    this.setDefaultState();
    initializeIcons();
  }

  private getAuthToken() : Promise<string> {
    if(!this.acquireTokenPromise && this.acquireTokenPromise == null) {
      this.acquireTokenPromise = this.props.acquireToken();
    }
    return this.acquireTokenPromise;
  }

  private getBotToken(authToken: string): Promise<IIrisBotToken>  {
    const requestUrl = this.props.irisApiBaseUrl + IrisConstants.TokenUrl;
    this.irisBotTokenPromise = this.httpRequest(requestUrl, "GET", authToken, null);
    return this.irisBotTokenPromise;
  }

  private httpRequest(url: string, type: string, token: string, data: any, headers?: Map<string, string>): Promise<any>  {
    return new Promise<any>((resolve, reject) => {
        let requestData = data
        if (type !== 'GET' && type !== 'DELETE') {
            requestData = JSON.stringify(data);
        }

        const ajaxRequest = new XMLHttpRequest();
        ajaxRequest.open(type.toUpperCase(), url, true);
        ajaxRequest.setRequestHeader('Content-Type', 'application/json');
        ajaxRequest.setRequestHeader('Authorization', `Bearer ${token}`);
        ajaxRequest.setRequestHeader('Access-Control-Allow-Origin', '*');
        if (headers !== undefined) {
            for (let [key, value] of Array.from(headers.entries())) {
                ajaxRequest.setRequestHeader(key, value);
            }
        }
        ajaxRequest.onreadystatechange = () => {
            if (ajaxRequest.readyState === 4) {
                if (ajaxRequest.status !== 200) {
                    reject(ajaxRequest.responseText);
                } else {
                  let response = JSON.parse(JSON.stringify({}));
                  try {
                    response = JSON.parse(ajaxRequest.responseText);
                  }
                  catch {
                    response = JSON.parse(JSON.stringify({}));
                  }
                  resolve(response);
                }
            }
        }
       ajaxRequest.send(requestData);
    })
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.log(`Error caught during rendering: ${error}, ${errorInfo}`);
  }

  public async componentDidMount() {
    await this.launchIrisBot();
    window.addEventListener('resize', this.handleResize);
  }

  public handleResize = () => {
    this.setState({ windowWidth: window.innerWidth, windowHeight: window.innerHeight });
  }

  public dismissPanel() {
    try {
      this.logFeatureUsageEvent('CloseChatWindow');
    }
    catch(e) {
      this.logException('dismissPanel', undefined, e);
    }
    this.setState({ isOpenPanel: false });
    if(this.props.closeBot) {
      this.props.closeBot();
    }
  }

  public render(): React.ReactElement<IIrisBotProps> {
    const customActivityMiddleware = () => (next: (arg0: any) => any) => (args: any) => {
      this.disableClickedButtons();
      return next(args);
    };

    const attachmentMiddleware = () => (next: (arg0: any) => any) => (args: any) => {
      if (args.attachment.contentType === 'application/vnd.microsoft.card.custom'){
        args.attachment.contentType = 'application/vnd.microsoft.card.adaptive'
      }
      return next(args)
    };

    let botAvatarUrl = IrisConstants.MainIconUrl;
    if (this.props.style != null && this.props.style.avatarIconUrl != null) {
      botAvatarUrl = this.props.style.avatarIconUrl;
    }

    return (
      <div className={styles.irisBot}>
        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.column}></div>
            <Draggable handle=".iris-header" enableUserSelectHack={false} bounds={{ left: -this.state.windowWidth + 480, right: 0, top: -160, bottom: this.state.windowHeight - 350 }} >
              <Panel isOpen={this.state.isOpenPanel} hasCloseButton={false} className={'irisBot ' + styles.irisChatWindow} type={PanelType.custom} isLightDismiss={true} isBlocking={false}>
                <div className={styles.modalDialog} role='document'>
                  <div className={styles.modalContent}>
                    {this.getBotWindowHeader()}
                    <div className={'modalBody ' + styles.modalBody} onClick={() => this.hideMenu()}>
                      {this.state.isInitializing === false && this.directLine != null &&
                        <ReactWebChat store={this.store} directLine={this.directLine} userID={this.state.uniqueId.toString()} activityMiddleware={customActivityMiddleware} attachmentMiddleware={attachmentMiddleware}
                          styleOptions={{
                            avatarSize: 25,
                            botAvatarInitials: 'IS',                          
                            botAvatarImage: botAvatarUrl,
                            botAvatarBackgroundColor: '#EDEBE9',
                            backgroundColor: '#EDEBE9',
                            bubbleBackground: '#ffffff',
                            bubbleBorderRadius: 7,
                            bubbleTextColor: '#323130',
                            bubbleFromUserBackground: '#EBEFFF',
                            bubbleFromUserBorderRadius: 7,
                            bubbleFromUserTextColor: '#242424',
                            suggestedActionBackground: '#EBEFFF',
                            suggestedActionTextColor: '#424242',
                            sendBoxHeight: 40,
                            sendBoxTextWrap: true,
                            hideScrollToEndButton: true,
                            sendBoxButtonShadeBorderRadius:10,
                            suggestedActionBorderRadius: 10,
                            suggestedActionLayout: 'stacked',
                            suggestedActionBorderStyle: 'solid',
                            suggestedActionBorderWidth: 1,
                            suggestedActionBackgroundColor: 'White',
                          }}
                          overrideLocalizedStrings= {{
                          TEXT_INPUT_PLACEHOLDER: "Ask a work question or make a request"
                        }}
                        />
                      }
                      {this.state.isInitializing === true &&
                        <div className={styles.spinner}>
                          <img src={IrisConstants.SpinnerImageUrl} alt="Loading Iris Copilot..." />
                          <p>Loading Iris...</p>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </Panel>
            </Draggable>
          </div>
        </div>
      </div>
    );
  }

  private getBotWindowHeader(): JSX.Element {
    let chatHeaderBgColor = '#EDEBE9';
    return (
      <div className={'iris-header ' + styles.modalHeader} id={'irisMenu'} style={{ background: chatHeaderBgColor }}>
        <img src={IrisConstants.MainIconUrl} id="iris-title-icon" alt="Iris title icon"/>
        <span id="iris-title-text">{IrisConstants.ChatWindowHeaderTitle}</span>
        <div className={styles.headerIcon}>
          { this.props.enableWindowResizing && <button type='button' title='Expand' onClick={() => { this.expandOrCondenseWindow(); }} aria-label='Expand' role="button">
            <Icon iconName='ChromeFullScreen' />
          </button> }
          { this.props.enableWindowResizing && <button type='button' title='Condense' style={{display: 'none'}} onClick={() => { this.expandOrCondenseWindow(); }} aria-label='Condense' role="button">
            <Icon iconName='BackToWindow' /> 
          </button> }
          <button type='button' title={this.props.refreshIconText ?? 'Start Over'} onClick={() => { this.restartIrisBot(); }} aria-label={this.props.refreshIconText ?? 'Start Over'} role="button">
            <Icon iconName='Refresh' />
          </button>
          { !this.props.hideCloseButton && <button type='button' title='Close' onClick={() => this.dismissPanel()} aria-label='Close' role="button">
            <Icon iconName='Cancel' />
          </button> }
        </div>
      </div>
    );
  }

  private setDefaultState() {
    this.state = { isInitializing: true, isOpenPanel: false, uniqueId: window.crypto.getRandomValues(new Uint32Array(1))[0], isWelcomeEventPosted: false, windowHeight: window.innerHeight, windowWidth: window.innerWidth };
  }

  private async launchIrisBot() {
    await this.initializeDirectLine();
    this.setState({ isInitializing: false, isOpenPanel: true, isWelcomeEventPosted: false });
    this.initiateIrisActivity();
  }

  private async initializeDirectLine() {
    this.authToken = await this.getAuthToken();
    this.irisBotToken = await this.getBotToken(this.authToken);
    this.directLine = new DirectLine({
      token: this.irisBotToken.token,
      webSocket: true
    });

    this.store = window["WebChat"].createStore();
    var context = await this.getContext();
    localStorage["iris-appContext"] = JSON.stringify(context);
    setTimeout(() => {
      this.buildAutocomplete(this.store);
    }, 5000);
  }

  private initiateIrisActivity() {
    const self = this;
    if (!!self.directLine && self.directLine === null) {
      return;
    }

    const irisSelfHelp: ISelfHelpContext  = {
      selfHelpEventName : (self.props.selfHelpContext && self.props.selfHelpContext!.selfHelpEventName) ?? '',
      messages : (self.props.selfHelpContext && self.props.selfHelpContext!.messages) ?? [],
      activityContext: (self.props.selfHelpContext && self.props.selfHelpContext!.activityContext) ?? {}
    }

    self.currentCount = 0;
    self.directLine.postActivity({
      from: { id: 'u_' + self.state.uniqueId.toString() },
      type: 'event',
      name: 'IrisEvent',
      value: {
        app: self.props.appName,
        token: self.irisBotToken.graphToken,
        botAppToken: self.irisBotToken.botAppToken,
        platform: self.props.platform,
        selfHelpEventName: irisSelfHelp.selfHelpEventName,
        activityContext: irisSelfHelp.activityContext,
        defaultQuery: irisSelfHelp.messages.length > 0 ? irisSelfHelp.messages[0] : null,
        pageName: window.location.href,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeOffSet: (new Date().getTimezoneOffset() * -1)
      }
    })
    .subscribe(
      (id: any) => {
        console.log(`Posted activity, assigned id ${id}`);
        self.setState({ isInitializing: false, isWelcomeEventPosted: true });
        setTimeout(() => {
          self.postCustomAppActivity(irisSelfHelp);
        }, 200);
        self.setChatBoxFocus();
      },
      (exception: any) => {
        this.logException('initiateIrisActivity', undefined, exception);
      }
    );
  }

  private async restartIrisBot() {
    this.setState({ isInitializing: true, isWelcomeEventPosted: false });
    await this.launchIrisBot();
    this.logFeatureUsageEvent('StartOver');
  }

  private async hideMenu() {
    let menu = document.getElementById('irisMenu')?.querySelector('ul');
    if (menu !== null && menu !== undefined) {
      menu.style.display = 'none';
    }
    return;
  }

  private setChatBoxFocus(): void {
    const chatBox = document.querySelector('textarea[aria-label="Message input box"]') as HTMLTextAreaElement;
    if (chatBox !== null || chatBox !== undefined) {
        chatBox.focus();
    }
}

  private disableClickedButtons(): void {
    const activities = document.querySelectorAll('section[aria-roledescription="chat history"] > article');
    if (activities === null || activities.length <= 0) {
      return;
    }

    const avatarContainer = activities[0].querySelector('div[class="webchat__stacked-layout__avatar-gutter"]');
    if (avatarContainer !== null && avatarContainer.childNodes.length <= 0) {
      avatarContainer.innerHTML = this.botAvatarView;
    }

    if (avatarContainer !== null && avatarContainer.childNodes.length > 0) {
      this.botAvatarView = avatarContainer.innerHTML;
    }

    let currentIndex = 1;
    this.currentCount = this.currentCount + 1;
    activities.forEach((activity) => {
      if (currentIndex === activities.length) {
        activity.querySelectorAll('button[class="ac-pushButton style-default ac-selectable"]').forEach(elem => {
          elem.setAttribute('title', elem.getAttribute('aria-label') || '');
        });
      } else {
        this.disableActivityButtons(activity);
        activity.querySelectorAll('select').forEach(elem => {
          elem.addEventListener('change', () => {
            this.disableActivityButtons(activity);
          });
        });
      }

      this.showScreenshotPreview(activity);
      currentIndex++;
    });

    setTimeout(() => {
      this.setChatBoxFocus();
    }, 2000);
  }

  private disableActivityButtons(activity: Element): void {
    activity.querySelectorAll('button[class="ac-pushButton style-default ac-selectable"]').forEach(elem => {
      if (elem.getAttribute('aria-label') !== 'View Ticket') {
        elem.setAttribute('title', elem.getAttribute('aria-label') || '');
        elem.setAttribute('disabled', 'disabled');
      }
    });
  }

  private showScreenshotPreview(activity: Element): void {
    const chkScreenshot = activity.querySelector('div[id="includeScreenshot"] input') as HTMLInputElement;
    if (chkScreenshot !== null) {
      const isEventAdded = chkScreenshot.getAttribute('isEventAdded');
      if (isEventAdded === null) {
        chkScreenshot.setAttribute('isEventAdded', 'Yes');
        const inputElement = activity.querySelector('input[aria-label="Screenshot"]') as HTMLInputElement;
        const imageContainer = activity.querySelector('div[id="previewScreenshot"]') as HTMLDivElement;
        chkScreenshot.addEventListener('click', () => {
          this.captureScreenshot(imageContainer, inputElement);
        });
      }
    }
  }

  private captureScreenshot(imageContainer: HTMLDivElement, inputElement: HTMLInputElement): void {
    const imageElement = imageContainer.querySelector('img[alt="Screenshot"]') as HTMLImageElement;
    if (inputElement.getAttribute('value') !== null) {
      imageContainer.setAttribute('style', imageContainer.getAttribute('style') + ';display:none');
      imageElement.setAttribute('src', IrisConstants.LoadingImageUrl);
      inputElement.removeAttribute('value');
      return;
    }

    imageElement.setAttribute('src', IrisConstants.LoadingImageUrl);
    imageContainer.setAttribute('style', imageContainer.getAttribute('style') + ';display:block');

    html2canvas(document.body).then(canvas => {
      let img = canvas.toDataURL('image/png');
      imageElement.setAttribute('src', img);
      inputElement.setAttribute('value', img.replace('data:image/png;base64,', ''));
      imageContainer.setAttribute('style', imageContainer.getAttribute('style') + ';display:block');
      try {
        var resizebase64 = require('resize-base64');
        var compressed = resizebase64(img, canvas.width / 2, canvas.height / 2);
        imageElement.setAttribute('src', compressed);
        inputElement.setAttribute('value', compressed.replace('data:image/png;base64,', ''));
      }
      catch (e) {
        this.logException('captureScreenshot', undefined, e);
      }
    });
  }

  private postBotActivity(activityMessage: string, useTextInput: boolean) {
    return new Promise((resolve, reject) => {
    this.directLine.postActivity({
      from: { id: 'u_' + this.state.uniqueId.toString() },
      type: 'message',
      text: useTextInput ? activityMessage : '',
      value: useTextInput ? {} : { message: activityMessage }
    })
    .subscribe(
      (id: any) => {
        console.log(`Posted custom activity, assigned id ${id}`);
        resolve(true);
      },
      (exception: any) => {
        this.logException('postBotActivity', undefined, exception);
        reject(exception);
      }
    );
    });
  }

  private buildAutocomplete(store: any): void {
    if(this.props.enableAutoSuggest){
      if (document.getElementsByClassName('webchat__send-box__main').length > 0) {
        let currentLI = 0;
        const textdiv = document.getElementsByClassName('webchat__send-box__main').item(0) as any;
        textdiv.parentElement.parentElement.style.position = 'relative';

        const textbox = document.getElementsByClassName('webchat__send-box-text-box').item(0) as HTMLElement;
        const autocomplete_dropdown = document.createElement('ul');
        autocomplete_dropdown.setAttribute('id', 'autocomplete_dropdown');
        autocomplete_dropdown.addEventListener('click', () => {
            // tslint:disable-next-line: no-shadowed-variable
            const textbox = document.getElementsByClassName('webchat__send-box-text-box').item(0) as HTMLElement;
            textbox[0].focus();
        });

        autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);

        const div = document.createElement('div');
        div.setAttribute('id', 'autocomplete_container');
        div.setAttribute('class', styles['autocompleteDiv']);
        div.appendChild(autocomplete_dropdown);
        const inserthere = document.getElementsByClassName('webchat__basic-transcript').item(0) as HTMLElement;
        inserthere.appendChild(div);

        let oldQuery = '';
        const self = this;

        textbox.addEventListener('keydown', async (event : KeyboardEvent) => {
            if(event.key == 'Backspace'){
                self.showAutoCompleteResult = false;
                autocomplete_dropdown.innerHTML = '';
                autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
            }
        });

        textbox.addEventListener('keyup', async (event : KeyboardEvent) => {
            self.showAutoCompleteResult = true;
            const query = textbox[0].textContent;
            if (query.length === 0) {
                self.showAutoCompleteResult = false;
                autocomplete_dropdown.innerHTML = '';
                autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                return;
            }
            if(event.key == ' '){
                self.showAutoCompleteResult = false;
                autocomplete_dropdown.innerHTML = '';
                autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                return;
            }
            else if(oldQuery.trim().toLocaleLowerCase() !== query.trim().toLocaleLowerCase()){
                if (textbox[0].textContent.length >= 3) {
                    const options = this.getAutocompleteOptions(textbox[0].textContent);
                    options.then((result)  => {
                        currentLI = 0;
                        autocomplete_dropdown.innerHTML = '';
                        autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                        if(result.length > 0 && self.showAutoCompleteResult && textbox[0].textContent.length >= 3) {
                            for (let i = 0; i < result.length; ++i) {
                                const entry = document.createElement('li');
                                entry.setAttribute('class', styles['autocompleteLi']);
                                const btn = document.createElement('BUTTON');
                                btn.setAttribute('class', styles['autocompleteButton']);
                                btn.innerHTML = result[i];

                          btn.addEventListener('click', btnEvent => {
                            const val = btnEvent.target as HTMLElement;
                                    store.dispatch({
                                        type: 'WEB_CHAT/SET_SEND_BOX',
                                        payload: {
                                            text: val["textContent"],
                                        }
                                    });
                                    autocomplete_dropdown.innerHTML = '';
                                    autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                                    oldQuery = val["textContent"] ?? '';
                                    self.showAutoCompleteResult = false;
                                    self.logAutoCompleteUsage(oldQuery);
                                });
                                entry.appendChild(btn);
                                autocomplete_dropdown.appendChild(entry);
                            }
                            autocomplete_dropdown.setAttribute('class', styles['autocompleteContainer']);
                        }
                    }).catch((data) => {
                        this.logException('buildAutoComplete', undefined, data);
                        self.showAutoCompleteResult = false;
                        autocomplete_dropdown.innerHTML = '';
                        autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                    });
                    oldQuery = query;
                } else {
                    autocomplete_dropdown.innerHTML = '';
                    autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                    self.showAutoCompleteResult = false;
                }
            }
        });

        document.addEventListener('keydown', async (event)  => {
            const listOptions = autocomplete_dropdown.getElementsByTagName('li');
            if (listOptions.length > 0) {
                switch (event.key) {
                    case 'ArrowUp':
                        listOptions[currentLI].setAttribute('class', styles['autocompleteLi']);
                        currentLI = currentLI > 0 ? --currentLI : listOptions.length - 1;
                        listOptions[currentLI].setAttribute('class', styles['autocompleteLiSelected']);
                        break;
                    case 'ArrowDown':
                        listOptions[currentLI].setAttribute('class', styles['autocompleteLi']);
                        currentLI = currentLI < listOptions.length - 1 ? ++currentLI : 0;
                        listOptions[currentLI].setAttribute('class', styles['autocompleteLiSelected']);
                        break;
                    case 'Enter':
                        const val = listOptions[currentLI].innerText;
                        const query = textbox[0].textContent;
                        oldQuery = val;
                        self.showAutoCompleteResult = false;
                        if (listOptions[currentLI].className === styles['autocompleteLiSelected']) {
                            event.preventDefault();
                            self.logAutoCompleteUsage(val);
                            store.dispatch({
                                type: 'WEB_CHAT/SET_SEND_BOX',
                                payload: {
                                    text: val
                                }
                            });
                        } else {
                            store.dispatch({
                                type: 'WEB_CHAT/SET_SEND_BOX',
                                payload: {
                                    text: query
                                }
                            });
                        }
                        autocomplete_dropdown.innerHTML = '';
                        autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
                }
            }
        });
        autocomplete_dropdown.focus();

        const sendButton = document.querySelector('[title="Send"]') as HTMLElement;
        sendButton.addEventListener('click', () => {
            autocomplete_dropdown.innerHTML = '';
            autocomplete_dropdown.setAttribute('class', styles['autocompleteContainerCollapsed']);
        });
    }
    }
  }

  private async getAutocompleteOptions(input: string): Promise<string[]> {
    let headers = new Map<string, string>();
    let contextInfo = JSON.parse(localStorage['iris-appContext']);
    headers.set('x-word', input);
    headers.set('x-isRoleorApp', contextInfo.roleOrApp);
    headers.set('x-roleOrAppName', contextInfo.roleOrAppName);
    headers.set('x-supportArea',contextInfo.supportArea);
    const requestUrl = this.props.irisApiBaseUrl + IrisConstants.AutoCompleteUrl;
    return await this.httpRequest(requestUrl, 'GET', this.authToken, null, headers);
  }

  private async getContext(): Promise<string[]> {
    let headers = new Map<string, string>();
    headers.set('x-appName', this.props.appName);
    const requestUrl = this.props.irisApiBaseUrl + IrisConstants.ContextUrl;
    return await this.httpRequest(requestUrl, 'GET', this.authToken, null, headers);
  }

  private async logFeatureUsageEvent(actionText: any, customProps?: Map<string, string>) {
    const correlationId = Guid.create().toString();
    let headers = new Map<string, string>();
    headers.set('CorrelationId', correlationId);
    headers.set('Content-Type', 'application/json');

    const telemetryEvent = this.getTelemetryEvent(actionText, correlationId, customProps);
    const requestUrl = this.props.irisApiBaseUrl + IrisConstants.TelemetryUrl;
    await this.httpRequest(requestUrl, 'POST', this.authToken, telemetryEvent, headers);
  }

  private getTelemetryEvent(actionText: string, correlationId: string, customProps?: Map<string, string>): any {
    const contextInfo = localStorage['iris-appContext'] !== undefined ? JSON.parse(localStorage['iris-appContext']) : { 'supportArea': 'answershub' };
    const telemetryEvent = {
      name: 'Iris.IrisBot-'+ actionText +'-ButtonClick-User',
      customProperties: {
         'CorrelationId': correlationId,
         'AppName': this.props.appName,
         "PageLinkOrName": location.href,
         'SupportArea': contextInfo.supportArea
      }
    };

    if(!!customProps  && customProps !== null) {
      customProps.forEach((value, key) => {
          telemetryEvent.customProperties[key] = value;
      });
    }
    return telemetryEvent;
  }

  private async logException(actionText: any, customProps?: Map<string, string>, exception?: any) {
    const correlationId = Guid.create().toString();
    let headers = new Map<string, string>();
    headers.set('CorrelationId', correlationId);
    headers.set('Content-Type', 'application/json');

    const telemetryException = this.getTelemetryException(actionText, correlationId, customProps, exception);
    const requestUrl = this.props.irisApiBaseUrl + IrisConstants.TelemetryExceptionUrl;
    await this.httpRequest(requestUrl, 'POST', this.authToken, telemetryException, headers);
  }

  private getTelemetryException(actionText: string, correlationId: string, customProps?: Map<string, string>, exception?: any): any {
    const contextInfo = localStorage['iris-appContext'] !== undefined ? JSON.parse(localStorage['iris-appContext']) : { 'supportArea': 'answershub' };
    const telemetryException = {
      customProperties: {
         'name': 'Iris.IrisBot-'+ actionText +'-ButtonClick-User',
         'CorrelationId': correlationId,
         'AppName': this.props.appName,
         "PageLinkOrName": location.href,
         'SupportArea': contextInfo.supportArea
      },
      exception: exception
    };

    if(!!customProps  && customProps !== null) {
      customProps.forEach((value, key) => {
        telemetryException.customProperties[key] = value;
      });
    }
    telemetryException.exception = exception;
    return telemetryException;
  }

  private logAutoCompleteUsage(questionSelected: string) {
    const props = new Map<string, string>([
        ['QuestionSelected', questionSelected]
      ]);
    this.logFeatureUsageEvent('AutoComplete', props);
  }

  private async postCustomAppActivity(selfHelpContext: ISelfHelpContext) {
    if (selfHelpContext !== undefined && selfHelpContext !== null) {
        const messages = selfHelpContext.messages;
        if (Array.isArray(messages) && messages.length > 1) {
            this.postActivityRecursively(messages);
        }
    }
 }

 private postActivityRecursively(inputs: string[], count: number = 1) {
    this.postBotActivity(inputs[count], true).then(() => {
        count++;
        if (count < inputs.length) {
            this.postActivityRecursively(inputs, count);
        }
    });
 }

 private expandOrCondenseWindow() {
  const mainWindow = document.querySelector('div[class^="ms-Panel-main"]');
  const modalBody = document.querySelector('div[class^="modalBody"]');
  const expandButton = document.querySelector('button[title^="Expand"]') as HTMLButtonElement;
  const condenseButtons = document.querySelector('button[title^="Condense"]') as HTMLButtonElement;
  if (mainWindow != null && modalBody != null) {
    var className = mainWindow?.getAttribute("class");
    if (className !== null && className !== undefined && className.indexOf("ms-Panel-main-expand") === -1 ) {
      mainWindow?.setAttribute("class", className?.replace("ms-Panel-main", "ms-Panel-main-expand") + "");
      var modalBodyClassName = modalBody?.getAttribute("class");
      modalBody?.setAttribute("class", modalBodyClassName?.replace("modalBody", "modalBody-expand") + "");
      if (expandButton !== null && expandButton !== undefined) {
        expandButton.style.display = 'none';
      }
      if (condenseButtons !== null && condenseButtons !== undefined) {
        condenseButtons.removeAttribute('style');
      }
    }
    else {
      mainWindow?.setAttribute("class", className?.replace("ms-Panel-main-expand", "ms-Panel-main") + "");
      var modalBodyClassName = modalBody?.getAttribute("class");
      modalBody?.setAttribute("class", modalBodyClassName?.replace("modalBody-expand", "modalBody") + "");
      if (expandButton !== null && expandButton !== undefined) {
        expandButton.removeAttribute('style');
      }
      if (condenseButtons !== null && condenseButtons !== undefined) {
        condenseButtons.style.display = 'none';
      }
    }
  }
  this.logFeatureUsageEvent('ResizeWindow');
}
}
